import React, { useState } from "react";
import { Box, Typography, Avatar, Chip, Stack, CardMedia } from "@mui/material";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import YouTube from "react-youtube";
import { useAuth } from "../../hooks";
import GuestSignInModal from "../../pages/Auth/GuestSignInModal";
import { NewSmallGradientButton } from "../Wallet/WalletDashboard";
import { CalendarIcon } from "@mui/x-date-pickers";
import { AccessTime } from "@mui/icons-material";
import { Icons } from "../assets/icon";
import dayjs from "dayjs";

interface Props {
  title: string;
  thumbnail: string;
  isLive: boolean;
  hasAccess: boolean;
  ticketPrice: number;
  onJoin: () => void;
  onPurchase: () => void;
  youtubeUrl?: string;
  disabled?: boolean;
  shortDescription: string;
  talentId: string;
  username: string;
  profileImage: string;
  startTime: any;
  endTime: any;
}

const CloverLiveEventCard: React.FC<Props> = ({
  title,
  thumbnail,
  isLive,
  hasAccess,
  ticketPrice,
  onJoin,
  onPurchase,
  shortDescription,
  youtubeUrl,
  disabled = false,
  talentId,
  username,
  profileImage,
  startTime,
  endTime,
}) => {
  const [showVideo, setShowVideo] = useState(false);
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const { userInfo } = useAuth();
  const isGuest = !userInfo;

  const handleJoinWithVideo = () => {
    setShowVideo(true);
    onJoin();
  };

  return (
    <Box
      sx={{
        width: "100%",
        // maxWidth: { xs: "100%", sm: 260 },
        maxWidth: { xs: 160, sm: 250 },
        minWidth: { xs: 160, sm: 250 },
        borderRadius: 2,
        boxShadow: "0 1px 6px rgba(0,0,0,0.1)",
        overflow: "hidden",
        backgroundColor: "background.paper",
        display: "flex",
        flexDirection: "column",
        pointerEvents: disabled ? "none" : "auto",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {/* Thumbnail */}
      <Box sx={{ position: "relative" }}>
        {!showVideo ? (
          <CardMedia
            component="img"
            image={thumbnail}
            alt={title}
            sx={{
              width: "100%",
              height: { xs: 140, sm: 120 },
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <YouTube
            videoId={extractYoutubeId(youtubeUrl || "")}
            opts={{
              width: "100%",
              height: "120",
              playerVars: {
                autoplay: 1,
                controls: 1,
                modestbranding: 1,
                rel: 0,
              },
            }}
          />
        )}
        {isLive && (
          <Chip
            label="LIVE"
            color="error"
            icon={<LiveTvIcon sx={{ fontSize: 14 }} />}
            size="small"
            sx={{
              position: "absolute",
              top: 6,
              left: 6,
              fontSize: "0.65rem",
              px: 1,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              lineHeight: 1,
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          display: "flex",
          flexDirection: "column",
          gap: 1,
          height: "100%",
        }}
      >
        {/* Title & Description */}
        <Typography
          variant="subtitle1"
          fontWeight={600}
          noWrap
          sx={{ fontSize: { xs: "0.95rem", sm: "1rem" } }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: "0.75rem",
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            minHeight: `calc(0.75rem * 1.3 * 2)`,
          }}
        >
          {shortDescription}
        </Typography>

        {/* Meta Info */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ fontSize: "0.75rem" }}
        >
          <CalendarIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" color="text.secondary">
            {dayjs(startTime).format("DD MMM")}
          </Typography>

          <AccessTime sx={{ fontSize: 14 }} />
          <Typography variant="caption" color="text.secondary">
            {dayjs(startTime).format("hh:mm A")}
          </Typography>

          <Icons.Coin fontSize="small" />
          <Typography
            variant="caption"
            sx={{ color: "var(--primary-700)", fontWeight: 600 }}
          >
            {ticketPrice}
          </Typography>
        </Stack>

        {/* Footer */}
        <Box
          sx={{
            mt: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Avatar
              src={profileImage}
              alt={username}
              sx={{ width: 28, height: 28 }}
            />
            <Typography
              variant="caption"
              noWrap
              sx={{
                maxWidth: { xs: "100%", sm: 90 },
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {username}
            </Typography>
          </Stack>

          <NewSmallGradientButton
            variant="contained"
            size="small"
            onClick={
              !isGuest ? handleJoinWithVideo : () => setIsGuestModalOpen(true)
            }
            startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 1.5,
              py: 0.75,
              fontSize: "0.7rem",
              borderRadius: 2,
              minWidth: { xs: "100%", sm: "auto" },
            }}
          >
            Join
          </NewSmallGradientButton>
        </Box>
      </Box>

      <GuestSignInModal
        open={isGuestModalOpen}
        onClose={() => setIsGuestModalOpen(false)}
      />
    </Box>
  );
};

// Helper to extract YouTube ID
function extractYoutubeId(url: string): string {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^\s&]+)/
  );
  return match?.[1] || "";
}

export default CloverLiveEventCard;
